<template>
  <AppHead :title="title" :description="description" :keywords="keywords" />

  <header class="bg-theme-paper">
    <div v-if="layoutType != 'candidate'" class="flex items-center justify-between px-4 mx-auto mb-1 lg:mb-3">
       <h1
          class="text-base font-bold lg:text-xl text-theme-primary"
        >
          {{ $t('Job Listings') }}
        </h1>

      <div v-if="hasPermissions(USER_PERMISSIONS.HR)">
        <GenericButton
          :isLink="true"
			 size="medium"
			 theme="secondary"
          to="/add-job"
          :text="$t('Add a new job')"
          v-if="layoutType != 'candidate'"
        />
      </div>

    </div>

    <Breadcrumbs :items="breadcrumbItems" />
  </header>
  <main class="flex flex-col flex-auto px-4 pt-8 lg:pt-14">
    <div class="flex flex-col gap-2 md-6 lg:mb-8">
      <div
        class="flex flex-col justify-between gap-4 sm:flex-row sm:justify-between sm:items-center"
      >
        <CategoriesFilter
          :filters="selectedFilters"
          :isMultiple="false"
          :is-active="isActive"
          @open="isActive = true"
          @loadingStart="loading = true"
        />
      </div>
      <div class="ml-1">
        <SearchTypeSelector
          :initialSearchType="searchType"
          @update:search-type="updateSearchType"
          ref="searchTypeSelector"
        />
      </div>
      <!-- Notification for search fallback -->
      <div
        v-if="searchFallbackInfo"
        class="p-3 mt-2 text-sm text-blue-800 border border-blue-300 rounded-lg bg-blue-50 dark:bg-blue-900 dark:text-blue-400 dark:border-blue-800"
      >
        <div class="flex items-center gap-2">
          <Icon class="text-secondary" type="info" color="currentColor" outer-class="w-5 h-5" />
          <span>{{ searchFallbackInfo.message }}</span>
        </div>
      </div>
    </div>
    <div class="flex flex-col flex-auto gap-4 xl:flex-row">
      <div class="w-full xl:max-w-64">
        <div
          @click="isActive = !isActive"
          class="bg-black bg-opacity-45 z-modal xl:z-auto group/modal invisible [&.active]:visible fixed opacity-0 duration-normal [&.active]:opacity-100 transition-all w-full h-full top-0 left-0 xl:relative xl:visible xl:opacity-100 xl:bg-transparent"
          :class="{ active: isActive }"
        >
          <div
            @click="isActive = !isActive"
            class="flex fixed h-full xl:h-fit flex-col group border w-64 -right-64 top-0 bg-white dark:bg-theme-paper border-disabled/20 dark:border-border-theme-divider duration-normal xl:rounded-lg overflow-hidden group-[&.active]/modal:right-0 transition-all xl:right-0 xl:sticky xl:top-1"
          >
            <div
              class="flex items-center justify-between border-b px-3 py-5 lg:border-disabled/20 border-transparent dark:border-border-theme-divider [&.active]:border-disabled/20 mx-2"
              :class="{ active: isActive }"
            >
              <h6 class="text-lg font-bold text-theme-primary">
                {{ $t('Filters') }}
              </h6>
              <div class="flex flex-row-reverse items-center gap-4">
                <div class="cursor-pointer" @click.stop="clearFilters">
                  <Icon type="filter-refresh" />
                </div>
              </div>
            </div>
            <div class="p-6 pb-0 xl:hidden">
              <GenericButton theme="outline" outerClass="w-full">
                {{ $t(`Apply Filters`) }}
              </GenericButton>
            </div>
            <div class="flex-auto px-5 py-6 overflow-auto xl:max-h-[calc(100vh_-_200px)]">
              <div class="flex flex-col gap-4 bg-white dark:bg-theme-paper">
                <h6 class="text-sm font-semibold text-theme-primary dark:text-theme-primary">
                  {{ $t(`Employment Types`) }}
                </h6>
                <ul class="flex flex-col gap-3 px-2">
                  <li
                    class="flex items-center gap-2.5"
                    v-for="(filter, index) in filters.employmentTypes"
                    :key="index"
                  >
                    <label class="flex items-center gap-2.5 text-sm cursor-pointer text-theme-primary">
                      <Checkbox
                        :value="index"
                        :checked="activeFilter('employmentTypes', index)"
                        @update:checked="handleFilter('employmentTypes', index)"
                      />
                      {{ $t(filter) }}
                    </label>
                  </li>
                </ul>
                <h6 class="text-sm font-semibold text-theme-primary dark:text-theme-primary">
                  {{ $t('Experience Level') }}
                </h6>
                <ul class="flex flex-col gap-3 px-2">
                  <li
                    class="flex items-center gap-2.5"
                    v-for="(experience, index) in filters.experienceLevels"
                    :key="index"
                  >
                    <label class="flex items-center gap-2.5 text-sm cursor-pointer text-theme-primary">
                      <input
                        type="radio"
                        name="experience"
                        :value="index"
                        :checked="activeFilter('experienceLevels', index)"
                        class="border border-grey-300 shadow dark:border-grey-600 text-main dark:text-main-light focus:ring-primary dark:focus:ring-primary dark:bg-grey-700"
                        @change="handleFilter('experienceLevels', index)"
                      />
                      {{ $t(experience) }}
                    </label>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="loading" class="grid w-full grid-cols-1 gap-6 h-fit" aria-busy="true">
        <!-- <LoadingSpinner /> -->
        <span class="sr-only" role="status">{{ $t('Loading jobs') }}</span>
        <div
          v-for="index in 6"
          :key="index"
          class="flex flex-col gap-3 p-4 border shadow-sm rounded-xl bg-theme-paper border-divider-theme-12 animate-pulse"
          aria-hidden="true"
          data-testid="job-card-skeleton"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <div class="w-8 h-8 rounded-xl bg-theme-neutral shrink-0"></div>
              <div class="space-y-1.5">
                <div class="w-56 h-3.5 rounded bg-theme-neutral"></div>
                <div class="w-32 h-3 rounded bg-theme-neutral"></div>
              </div>
            </div>
            <div class="flex gap-2">
              <div class="w-5 h-5 rounded bg-theme-neutral"></div>
              <div class="w-5 h-5 rounded bg-theme-neutral"></div>
            </div>
          </div>
          <div class="flex items-center justify-between">
            <div class="w-20 h-3 rounded bg-theme-neutral"></div>
            <div class="w-28 h-3 rounded bg-theme-neutral"></div>
          </div>
        </div>
      </div>
      <div
        class="grid w-full grid-cols-1 gap-6 h-fit"
        v-else-if="jobs.data.length > 0"
        itemscope
        itemtype="https://schema.org/ItemList"
      >
        <JobCard
          v-for="(job, index) in jobs.data"
          :key="job.id"
          :job="job"
          :userOrganizationId="userOrganizationId"
          @job-unsaved="handleJobUnsaved"
          @job-archived="handleJobArchived"
          @open-dialog="openDialog"
          itemprop="itemListElement"
          itemscope
          itemtype="https://schema.org/ListItem"
          :data-position="index + 1"
        />

        <ConfirmationModal
          v-if="hasPermissions(USER_PERMISSIONS.HR)"
          :show="dialog.open"
          @update:toggleModal="closeDialog"
          @confirmed="handleConfirm"
          :title="$t(dialog.title)"
          :message="$t(dialog.message, { app: $page.props.appName })"
        />
      </div>
      <div class="w-full text-theme-primary" v-else>
        {{ $t('No jobs match your criteria, try different keywords.') }}
      </div>
    </div>
    <PaginationLinks :members="jobs" :paramsUrl="paramsUrl" />
  </main>
</template>

<script>
import GenericButton from '@/Components/GenericButton.vue';
import Checkbox from '@/Components/Checkbox.vue';
import PaginationLinks from '@/Components/PaginationLinks.vue';
import SearchTypeSelector from '@/Components/SearchTypeSelector.vue';
import Breadcrumbs from '@/Components/Breadcrumbs.vue';
import { USER_PERMISSIONS } from '@/constants.js';
import {
  EMPLOYMENT_TYPES,
  EXPERIENCE_LEVELS,
  EXPERIENCE_FILTER_ALL,
  toLabelMap,
} from '@/Constants/jobEnums.js';
import Icon from '@/Icons/OptimizedIcons.vue';
import CategoriesFilter from '@/Pages/Jobs/Partials/CategoriesFilter.vue';
import JobCard from '@/Pages/Jobs/Partials/JobCard.vue';
import { hasPermissions } from '@/Utils/permissions.js';
import { Link } from '@inertiajs/vue3';
import { defineAsyncComponent, ref, computed } from 'vue';
import HRLayout from '@/Layouts/HRLayout.vue';
import AuthenticatedLayout from '@/Layouts/AuthenticatedLayout.vue';
// import LoadingSpinner from '@/Components/LoadingSpinner.vue';

const ConfirmationModal = defineAsyncComponent(() => import('@/Components/ConfirmationModal.vue'));

export default {
  components: {
    SearchTypeSelector,
    // LoadingSpinner,
    Link,
    GenericButton,
    Checkbox,
    Icon,
    JobCard,
    CategoriesFilter,
    PaginationLinks,
    ConfirmationModal,
    Breadcrumbs,
  },
  props: {
    jobs: Object,
    layoutType: String,
    userOrganizationId: Number,
    title: String,
    description: String,
    keywords: String,
    selectedFilters: Object,
    searchFallbackInfo: {
      type: Object,
      default: null,
    },
  },
  data() {
    const params = new URLSearchParams(window.location.search);

    return {
      isActive: false,
      windowWidth: window.innerWidth,
      loading: false,
      selectedEmploymentTypes: params.get('employmentTypes')?.split(',').filter(Boolean) ?? [],
      selectedExperienceLevel: params.get('experienceLevels') || null,
      filters: {
        employmentTypes: toLabelMap(EMPLOYMENT_TYPES),
        experienceLevels: {
          [EXPERIENCE_FILTER_ALL.value]: EXPERIENCE_FILTER_ALL.labelKey,
          ...toLabelMap(EXPERIENCE_LEVELS),
        },
      },
      paramsUrl: ref(''),
      searchType: 'vector',
      dialog: {
        open: false,
        title: '',
        message: '',
        onConfirm: null,
      },
    };
  },
  computed: {
    canSeeBanner() {
      if (this.$page.props.auth.user && this.hasPermissions(USER_PERMISSIONS.HR)) {
        return true;
      }
      return false;
    },
    breadcrumbItems() {
      const currentPath = window.location.pathname;
      const items = [];

      if (this.layoutType === 'candidate') {
        // Candidate breadcrumbs: Jobs > All Jobs/Applied/Saved
        const candidateViews = [
          { label: this.$t('Applied'), href: '/jobs/applied', path: '/jobs/applied' },
          { label: this.$t('Saved'), href: '/jobs/saved', path: '/jobs/saved' },
        ];

        candidateViews.forEach(view => {
          if (currentPath === view.path) {
            items.push({ label: view.label, active: true });
          } else {
            items.push({ label: view.label, href: view.href });
          }
        });
      } else {
        const hrViews = [
          { label: this.$t('Active'), href: '/job-list/active', path: '/job-list/active' },
          { label: this.$t('Drafts'), href: '/job-list/draft', path: '/job-list/draft' },
          { label: this.$t('Archived'), href: '/job-list/archived', path: '/job-list/archived' },
        ];

        hrViews.forEach(view => {
          if (currentPath === view.path) {
            items.push({ label: view.label, active: true });
          } else {
            items.push({ label: view.label, href: view.href });
          }
        });
      }

      return items;
    },
  },
  methods: {
    isActiveUrl(url) {
      const activeUrl = ref(window.location.pathname);

      return activeUrl.value === url;
    },
    hasPermissions,
    handleJobUnsaved(jobId) {
      this.jobs.data = this.jobs.data.filter((job) => job.id !== jobId);
    },
    handleJobArchived(jobId) {
      // Remove the archived job from the list if we're not on the archived page
      const currentUrl = window.location.href;
      if (!currentUrl.includes('/archived')) {
        this.jobs.data = this.jobs.data.filter((job) => job.id !== jobId);
      }
    },
    handleFilter(filterType, index) {
      if (index === undefined) {
        return;
      }

      const value = index.toString();

      if (filterType === 'experienceLevels') {
        // Radio-style: selecting the active level again deselects it
        this.selectedExperienceLevel = this.selectedExperienceLevel === value ? null : value;
      } else if (this.selectedEmploymentTypes.includes(value)) {
        this.selectedEmploymentTypes = this.selectedEmploymentTypes.filter((val) => val !== value);
      } else {
        this.selectedEmploymentTypes = [...this.selectedEmploymentTypes, value];
      }

      this.applyFilters();
    },
    applyFilters() {
      this.loading = true;

      const params = new URLSearchParams(window.location.search);

      if (this.selectedEmploymentTypes.length > 0) {
        params.set('employmentTypes', this.selectedEmploymentTypes.join(','));
      } else {
        params.delete('employmentTypes');
      }

      if (this.selectedExperienceLevel) {
        params.set('experienceLevels', this.selectedExperienceLevel);
      } else {
        params.delete('experienceLevels');
      }

      params.delete('page');

      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;

      this.$inertia.get(newUrl, {}, {
        preserveScroll: true,
        onFinish: () => {
          this.loading = false;
        },
      });
    },
    activeFilter(filterType, index) {
      if (index === undefined) {
        return false;
      }

      const value = index.toString();

      return filterType === 'experienceLevels'
        ? this.selectedExperienceLevel === value
        : this.selectedEmploymentTypes.includes(value);
    },
    addGtmScript() {

      const script3 = document.createElement('script');
      script3.id = 'gtm-script3';
      script3.innerHTML = `
                function tag_conversion() {
                    try {
                        gtag('event', 'conversion', {
                            'send_to': 'AW-16689675207/ClHwCOneps8ZEMf3oJY-',
                            'value': 1.0,
                            'currency': 'CHF',
                            'transaction_id': ''
                        });
                    } catch (e) {
                        console.debug('Conversion tracking blocked by browser extension');
                    }
                } `;
      document.body.appendChild(script3);
    },
    removeGtmScript() {
      // const script1 = document.getElementById("gtm-script1");
      // const script2 = document.getElementById("gtm-script2");
      const script3 = document.getElementById('gtm-script3');
      // if (script1) document.body.removeChild(script1);
      // if (script2) document.body.removeChild(script2);
      if (script3) document.body.removeChild(script3);
    },
    removeQueryParams(queryString, paramToRemove) {
      const params = new URLSearchParams(queryString);
      params.delete(paramToRemove);
      return params.toString();
    },
    openDialog(action) {
      this.dialog = {
        open: true,
        title: action.title,
        message: action.message,
        onConfirm: action.onConfirm,
      };
    },
    closeDialog() {
      this.dialog.open = false;
    },

    handleConfirm() {
      if (typeof this.dialog.onConfirm === 'function') {
        this.dialog.onConfirm();
      }
      this.closeDialog();
    },
    updateSearchType(type) {
      this.searchType = type;

      const params = new URLSearchParams(window.location.search);
      if (type === 'vector') {
        params.delete('search_type');
      } else if (type === 'standard') {
        params.set('search_type', 'standard');
      }
      if (params.size > 0) {
        window.history.pushState({}, '', window.location.pathname + '?' + params.toString());
      } else {
        window.history.pushState({}, '', window.location.pathname);
      }
    },
    clearFilters() {
      if (this.selectedEmploymentTypes.length === 0 && !this.selectedExperienceLevel) {
        return;
      }

      this.selectedEmploymentTypes = [];
      this.selectedExperienceLevel = null;

      this.applyFilters();
    },
  },
  mounted() {
    // Add the Google Tag Manager script dynamically
    this.addGtmScript();
    const params = new URLSearchParams(window.location.search);

    const newParams = this.removeQueryParams(params.toString(), 'page');
    if (newParams !== '') {
      this.paramsUrl = '&' + newParams;
    }

    // Handle search fallback if provided from the backend
    if (this.searchFallbackInfo) {
      // Set search type to standard in the URL when falling back from vector search
      if (!params.get('search_type') || params.get('search_type') === 'vector') {
        params.set('search_type', 'standard');
        window.history.replaceState(
          {},
          '',
          window.location.pathname + (params.toString() ? '?' + params.toString() : '')
        );

        // Update the search type selector UI
        this.searchType = 'standard';

        // Use $nextTick to ensure the component is mounted
        this.$nextTick(() => {
          if (this.$refs.searchTypeSelector) {
            // Force the component to update its internal state
            this.$refs.searchTypeSelector.selectedType = 'standard';
          }
        });
      }
    }
  },
  beforeUnmount() {
    // Vue 3 syntax, use beforeDestroy for Vue 2
    // Optional: Clean up the GTM script if needed
    this.removeGtmScript();
  },
  layout: (h, page) => {
    return h(page.props.layoutType == 'hr' ? HRLayout : AuthenticatedLayout, {nav : page.props?.layoutType }, [h(page)]);
  },
};
</script>
